import {expectEitherAndCrash, HttpClient, usePublicHttpClient} from '@raison/network-facade';
import {createAbortControl, invariant} from '@raison/toolbox';
import {solveChallengeWorkers} from 'altcha-lib';
import {Challenge, Payload, Solution} from 'altcha-lib/types';
import {type} from 'arktype';
import {TaggedError} from 'effect/Data';
import * as Either from 'effect/Either';
import {identity} from 'effect/Function';
import {isString} from 'effect/Predicate';
import React, {createContext, Dispatch, PropsWithChildren, useContext, useEffect, useMemo, useReducer} from 'react';
import {ShellVersion} from '~/entities/versions';
import {env} from '~/environment';
import {createBeacon, createLogger} from '~/shared/logging';
import {isIOS, isMobileApp} from '~/shared/utils/mobileDeviceDetect';
import {getTurnstileToken, TurnstileError} from './TurnstileAPI';

const IS_ALTCHA_ENABLED = false;
const TIMEOUT = 20_000;
const EXPIRY = 60_000;
const FALLBACK_CONCURRENCY = 4;
const log = createLogger('PoW Captcha');

export interface CaptchaAPI {
    execute: () => Promise<CaptchaToken>;
    warmup: () => Promise<CaptchaToken>;
    reset: (reason?: CaptchaAbortTrigger) => void;
}

const CaptchaApiContext = createContext<null | CaptchaAPI>(null);
const CaptchaStateContext = createContext<null | CaptchaState>(null);

export function useCaptcha(): CaptchaAPI {
    const internalApi = useContext(CaptchaApiContext);
    invariant(internalApi !== null, 'useCaptcha requires CaptchaProvider');

    const api = useMemo(() => {
        let unmounted = false;
        const reason = CaptchaAbortTrigger.Unmount;
        return {
            ...internalApi,
            execute: () => (unmounted ? Promise.reject(new CaptchaIsAborted(reason)) : internalApi.execute()),
            warmup: () => (unmounted ? Promise.reject(new CaptchaIsAborted(reason)) : internalApi.warmup()),
            unmount() {
                internalApi.reset(reason);
                unmounted = true;
            },
        };
    }, [internalApi]);

    useEffect(() => () => api.unmount(), [api]);

    return api;
}

export function useCaptchaState() {
    const state = useContext(CaptchaStateContext);
    invariant(state !== null, 'useCaptchaState requires CaptchaProvider');
    return state;
}

export function CaptchaProvider({children}: PropsWithChildren) {
    const http = usePublicHttpClient();
    const [state, dispatch] = useReducer(reduceCaptchaState, {
        executeInitAt: 0,
        executeDoneAt: 0,
    });
    const api = useMemo(createCaptchaApi.bind(null, http, dispatch), [http]);
    return (
        <CaptchaApiContext.Provider value={api}>
            <CaptchaStateContext.Provider value={state}>{children}</CaptchaStateContext.Provider>
        </CaptchaApiContext.Provider>
    );
}

export interface CaptchaState {
    executeInitAt: number;
    executeDoneAt: number;
}

enum CaptchaRunMode {
    None = 0,
    Warmup = 1 << 0,
}
type CaptchaRunFlags = CaptchaRunMode | (number & {});

type CaptchaAction = {flag: CaptchaRunFlags} & ({type: 'trigger'} | {type: 'settled'});

function reduceCaptchaState(state: CaptchaState, {type, flag}: CaptchaAction): CaptchaState {
    const isWramup = flag & CaptchaRunMode.Warmup;
    if (isWramup) {
        return state;
    }
    switch (type) {
        case 'trigger':
            return {...state, executeInitAt: Date.now()};
        case 'settled':
            return {...state, executeDoneAt: Date.now()};
        default:
            return state;
    }
}

type CaptchaRuntime = Readonly<{
    id: number;
    promise: Promise<CaptchaToken>;
    flag: CaptchaRunFlags;
    abort: (reason: CaptchaIsAborted) => void;
}>;

function createCaptchaApi(http: HttpClient, dispatch: Dispatch<CaptchaAction>): CaptchaAPI {
    let runtime: CaptchaRuntime | null = null;
    let runtimeId = 0;
    let expiryTimeoutId = -1;

    function clear(runtimeId?: number) {
        window.clearTimeout(expiryTimeoutId);
        if (runtimeId === undefined || runtime?.id === runtimeId) {
            runtime = null;
        }
    }

    async function execute(flag: CaptchaRunFlags) {
        if (runtime && IS_ALTCHA_ENABLED) {
            const runtimeIsWarmup = Boolean(runtime.flag & CaptchaRunMode.Warmup);
            const runtimeIsHot = !runtimeIsWarmup;
            const callIsWarmup = Boolean(flag & CaptchaRunMode.Warmup);
            const callIsHot = !callIsWarmup;

            // intentionally left these conditions verbose because this whole system
            // has a lot of implicit states which complicate reasonning about it

            if (runtimeIsWarmup && callIsWarmup) {
                return runtime.promise;
            }
            if (runtimeIsHot && callIsHot) {
                return runtime.promise;
            }
            if (runtimeIsHot && callIsWarmup) {
                return runtime.promise;
            }
            if (runtimeIsWarmup && callIsHot) {
                dispatch({type: 'trigger', flag});
                return runtime.promise.finally(() => {
                    clear();
                    dispatch({type: 'settled', flag});
                });
            }
        }

        const id = runtimeId++;

        const [signal, abort] = createAbortControl<CaptchaIsAborted>();

        dispatch({type: 'trigger', flag});
        const promise = fetchAndSolveChallenge(http, {signal})
            .then(Either.getOrThrowWith(identity))
            .finally(() => {
                const wasWarmingUp = runtime && runtime.flag & CaptchaRunMode.Warmup;
                if (wasWarmingUp) {
                    expiryTimeoutId = window.setTimeout(() => clear(id), EXPIRY);
                } else {
                    clear(id);
                }
                dispatch({type: 'settled', flag});
            });

        runtime = {promise, flag, abort, id};
        return runtime.promise;
    }

    const self: CaptchaAPI = {
        execute() {
            return execute(CaptchaRunMode.None);
        },
        warmup() {
            return execute(CaptchaRunMode.Warmup);
        },
        reset(reason = CaptchaAbortTrigger.Reset) {
            runtime?.abort(new CaptchaIsAborted(reason));
            clear();
        },
    };
    return self;
}

class CaptchaRateLimit extends TaggedError('CaptchaRateLimit') {}
class CaptchaNotSolved extends TaggedError('CaptchaNotSolved')<{cause: unknown}> {}
class CaptchaIsAborted extends TaggedError('CaptchaIsAborted')<{trigger: CaptchaAbortTrigger}> {
    constructor(trigger: CaptchaAbortTrigger) {
        // Can't type AbortSignal reason, so have to settle for defensive programming
        invariant(isString(trigger) && trigger in CaptchaAbortTrigger, 'reason is not CaptchaAbortReason');
        super({trigger});
        this.message = `Captcha was aborted on ${trigger}`;
    }
}
class UnsupportedShellVersion extends TaggedError('UnsupportedShellVersion')<{version: string}> {}

export type CaptchaError =
    | CaptchaRateLimit
    | CaptchaNotSolved
    | CaptchaIsAborted
    | UnsupportedShellVersion
    | TurnstileError;

enum CaptchaAbortTrigger {
    Reset = 'Reset',
    Timeout = 'Timeout',
    Unmount = 'Unmount',
}

export function isCaptchaError(error: unknown): error is CaptchaError {
    return (
        error instanceof CaptchaIsAborted ||
        error instanceof CaptchaNotSolved ||
        error instanceof CaptchaRateLimit ||
        error instanceof UnsupportedShellVersion
    );
}

export function shouldThrowCaptchaError(error: CaptchaError) {
    return error instanceof CaptchaNotSolved;
}

async function fetchAndSolveChallenge(
    http: HttpClient,
    {signal}: HasSignal
): Promise<Either.Either<CaptchaToken, CaptchaError>> {
    const beacon = createBeacon(http);

    let time = performance.now();

    if (!IS_ALTCHA_ENABLED) {
        return await executeTurnstile(beacon, signal, time);
    }

    const response = await http('v1/public/challenge', {signal})
        .then(
            ...expectEitherAndCrash({
                200: ChallengeResponse,
                429: null,
            })
        )
        .finally(() => {
            beacon.gauge('react_app_challenge_load_ms', performance.now() - time);
        });

    if (response.status === 'Abort') {
        log.warn('Aborted at challenge request');
        return Either.left(new CaptchaIsAborted(signal.reason));
    }
    if (response.status === 'Crash') {
        log.warn('Failed at challenge request');
        return Either.left(new CaptchaNotSolved({cause: response.reason}));
    }
    if (response.status === 429) {
        return await executeTurnstile(beacon, signal, time);
    }

    const timeout = AbortSignalX.timeout(TIMEOUT, new CaptchaIsAborted(CaptchaAbortTrigger.Timeout));
    const {challenge} = response.json;
    const {promisedSolution, workersCount} = prepareAltchaSolution(challenge, {
        signal: AbortSignalX.race(signal, timeout),
    });
    log.info(`Got challenge, began solving on ${workersCount} workers`);
    beacon.count('react_app_default_captcha_use');
    time = performance.now();
    const solution = await promisedSolution
        .catch(error => {
            beacon.count('react_app_default_captcha_failure');
            throw error;
        })
        .finally(() => {
            beacon.gauge('react_app_default_captcha_perf_ms', performance.now() - time);
        });

    if (!solution) {
        if (signal.aborted) {
            log.warn('Aborted at solving');
            return Either.left(new CaptchaIsAborted(signal.reason));
        } else {
            log.fail('Failure at solving');
            return Either.left(new CaptchaNotSolved({cause: new Error('Solution not found')}));
        }
    }
    log.info('Got solution');

    return Either.right(encodeDefaultToken(challenge, solution));
}

async function executeTurnstile(beacon: ReturnType<typeof createBeacon>, signal: AbortSignal, time: number) {
    if (!doesSupportTurnstile() && !IS_ALTCHA_ENABLED) {
        const version = ShellVersion.getFromWindow();
        return Either.left(new UnsupportedShellVersion({version: version?.toString() ?? 'Unknown'}));
    }
    if (!doesSupportTurnstile()) {
        return Either.left(new CaptchaRateLimit());
    }
    if (IS_ALTCHA_ENABLED) {
        log.warn('Fallen back to Turnstile');
    }
    beacon.count('react_app_fallback_captcha_use');
    time = performance.now();
    const turnstileToken = await getTurnstileToken(env.TURNSTILE_SITEKEY, signal)
        .catch(error => {
            beacon.count('react_app_fallback_captcha_failure');
            throw error;
        })
        .finally(() => {
            beacon.gauge('react_app_fallback_captcha_perf_ms', performance.now() - time);
        });
    return Either.right(encodeFallbackToken(turnstileToken));
}

function doesSupportTurnstile() {
    if (isMobileApp()) {
        if (!isIOS()) {
            // only iOS had issues with turnstile
            return true;
        }
        const currentVersion = ShellVersion.getFromWindow();
        if (!currentVersion) {
            return false;
        }
        // turnstile issues on iOS were fixed in 2026.01
        return ShellVersion.compare(currentVersion, '>=', '2026.1');
    } else {
        return true;
    }
}

export type CaptchaToken = string & {
    [K in {readonly CaptchaToken: unique symbol}['CaptchaToken']]: void;
};

type AltchaHashAlgorithm = typeof AltchaHashAlgorithm.infer;
const AltchaHashAlgorithm = type.enumerated('SHA-1', 'SHA-256', 'SHA-512');

type OurAltchaChallengeDto = typeof OurAltchaChallengeDto.infer;
const OurAltchaChallengeDto = type({
    algorithm: AltchaHashAlgorithm,
    challenge: 'string',
    signature: 'string',
    salt: 'string',
    maxNumber: 'number',
}) satisfies {
    infer: Challenge;
};

const ChallengeResponse = type({
    challenge: OurAltchaChallengeDto,
});

function prepareAltchaSolution(dto: OurAltchaChallengeDto, opt: HasSignal) {
    const {challenge, salt, algorithm, maxNumber} = dto;
    const {signal} = opt;

    let finished = false;
    let workersCount = 0;
    const workerError = Promise.withResolvers<never>();
    const workerFactory = () => {
        const workerId = ++workersCount;
        const worker = createAltchaWorker();
        worker.addEventListener('error', () => {
            workerError.reject(new Error('Error in altcha worker'));
        });
        const handleAbort = () => {
            if (finished) {
                return;
            }
            log.info(`Aborting worker ${workerId}`);
            worker.postMessage({type: 'abort'});
            workerError.reject(signal.reason);
        };
        signal.addEventListener('abort', handleAbort, {once: true});
        return worker;
    };

    let concurrency: number;
    if ('hardwareConcurrency' in navigator) {
        concurrency = navigator.hardwareConcurrency;
    } else {
        concurrency = FALLBACK_CONCURRENCY;
    }
    const deferredSolution = solveChallengeWorkers(workerFactory, concurrency, challenge, salt, algorithm, maxNumber);
    deferredSolution.finally(() => {
        finished = true;
    });
    const promisedSolution = Promise.race([workerError.promise, deferredSolution]);
    return {
        promisedSolution,
        workersCount,
    };
}

function createAltchaWorker() {
    // Any sort of refactoring of this line causes Vite to fail to load the worker
    // it has to look exactly like in documentation https://vite.dev/guide/features#import-with-constructors
    // It pisses me off so much.
    return new Worker(new URL('altcha-lib/worker', import.meta.url), {
        type: 'module',
    });
}

function encodeFallbackToken(payload: string): CaptchaToken {
    return `mode=FALLBACK&token=${payload}` as CaptchaToken;
}

function encodeDefaultToken(challenge: Challenge, solution: Solution): CaptchaToken {
    const payloadObject = {
        algorithm: challenge.algorithm,
        challenge: challenge.challenge,
        number: solution.number,
        salt: challenge.salt,
        signature: challenge.signature,
    };
    const payload = btoa(JSON.stringify(payloadObject satisfies Payload));
    return `mode=DEFAULT&token=${payload}` as CaptchaToken;
}

namespace AbortSignalX {
    // 'abort' event can be dispatched without aborting the signal, thanks Brandon Eich

    export const race = (a: AbortSignal, b: AbortSignal) => {
        const [signal, abort] = createAbortControl();
        a.addEventListener('abort', () => {
            if (a.aborted) {
                abort(a.reason);
            }
        });
        b.addEventListener('abort', () => {
            if (b.aborted) {
                abort(b.reason);
            }
        });
        return signal;
    };

    export const timeout = (time: number, reason: CaptchaIsAborted) => {
        const [signal, abort] = createAbortControl<CaptchaIsAborted>();
        const timeoutId = window.setTimeout(() => {
            abort(reason);
        }, time);
        signal.addEventListener('abort', () => window.clearTimeout(timeoutId), {once: true});
        return signal;
    };
}

interface HasSignal {
    signal: AbortSignal;
}
